import { useQuery } from "@tanstack/react-query";
import type { ColumnDef } from "@tanstack/react-table";

import { DataTable } from "@/components/data";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Skeleton } from "@/components/ui/skeleton";

import { funnelsKey, getFunnels, messageFor } from "./api";
import { ALL_FUNNELS, FunnelControls, funnelLabel, selectedFunnel, useControls } from "./controls";
import { formatCount, funnelName } from "./format";
import { OrdersStamp, OrdersUnavailable } from "./OrderFigures";
import {
  getFunnelSources,
  getFunnelSourcesAll,
  statsSourcesKey,
  type AppSegment,
  type FunnelSourcesStats,
  type SourceOrders,
} from "./stats-api";
import { SourceName } from "./SourceName";
import { doorLabel, formatMoney, formatOrders, formatRate, sortedSources, sourceLabel } from "./stats-format";

const numeric = { meta: { align: "right" as const } };

const COLUMNS: ColumnDef<SourceOrders, unknown>[] = [
  {
    id: "source",
    header: "Source",
    accessorFn: (row) => sourceLabel(row.source),
    cell: ({ row }) => <SourceName source={row.original.source} />,
  },
  { id: "visitors", header: "Visitors", accessorFn: (row) => formatCount(row.visitors), ...numeric },
  { id: "signups", header: "Sign-ups", accessorFn: (row) => formatCount(row.leads), ...numeric },
  { id: "signup_rate", header: "Sign-up rate", accessorFn: (row) => formatRate(row.lead_rate), ...numeric },
  { id: "orders", header: "Orders", accessorFn: (row) => formatOrders(row.orders), ...numeric },
  { id: "order_rate", header: "Orders per sign-up", accessorFn: (row) => formatRate(row.order_rate), ...numeric },
  { id: "revenue", header: "Revenue", accessorFn: (row) => formatMoney(row.revenue_gbp), ...numeric },
  {
    id: "revenue_per_visitor",
    header: "Revenue per visitor",
    accessorFn: (row) => formatMoney(row.revenue_per_visitor),
    ...numeric,
  },
];

// In-app checkout orders (C8 R3): only the every-funnel answer carries them, because an app
// order has no funnel. They are a second lens, not a share of "Orders".
const APP_COLUMNS: ColumnDef<SourceOrders, unknown>[] = [
  { id: "app_orders", header: "In-app orders", accessorFn: (row) => formatOrders(row.app_orders ?? 0), ...numeric },
  { id: "app_revenue", header: "In-app revenue", accessorFn: (row) => formatMoney(row.app_revenue_gbp ?? 0), ...numeric },
];

const SEGMENT_COLUMNS: ColumnDef<AppSegment, unknown>[] = [
  {
    id: "source",
    header: "App source",
    accessorFn: (row) => sourceLabel(row.source),
    cell: ({ row }) => <SourceName source={row.original.source} />,
  },
  { id: "door", header: "Door", accessorFn: (row) => doorLabel(row.door) },
  { id: "orders", header: "In-app orders", accessorFn: (row) => formatOrders(row.orders), ...numeric },
  { id: "revenue", header: "In-app revenue", accessorFn: (row) => formatMoney(row.revenue_gbp), ...numeric },
];

/** The table's columns: the every-funnel answer adds the two in-app columns. */
export function sourceColumns(stats: FunnelSourcesStats | undefined): ColumnDef<SourceOrders, unknown>[] {
  return stats?.app ? [...COLUMNS, ...APP_COLUMNS] : COLUMNS;
}

/** The funnel's (or every funnel's) totals in one sentence, every figure as the service sent it. */
export function sourcesSummaryLine(stats: FunnelSourcesStats): string {
  const { totals } = stats;
  const orders = `${formatCount(totals.orders)} ${totals.orders === 1 ? "order" : "orders"}`;
  const who = stats.slug === null ? "All funnels" : funnelName(stats.slug);
  const line = `${who}, last ${stats.window.days} days: ${formatCount(totals.visitors)} visitors · ${formatCount(totals.leads)} sign-ups · ${orders} · ${formatMoney(totals.revenue_gbp)}`;
  if (!stats.app) return line;
  const app = stats.app.totals.orders;
  return `${line} · ${formatCount(app)} in-app ${app === 1 ? "order" : "orders"} (${formatMoney(stats.app.totals.revenue_gbp)})`;
}

/**
 * Traffic sources, for every funnel together (the default, GATE-379 FN-2) or one
 * funnel: who came, who signed up, who ordered, and what they spent. The funnel
 * and window controls are the Split test tab's, so the choice carries between
 * the two tabs in the URL.
 */
export function SourcesTab({ areaName }: { areaName: string }) {
  const controls = useControls();
  const funnels = useQuery({
    queryKey: funnelsKey(controls.days),
    queryFn: () => getFunnels(controls.days),
  });

  const list = funnels.data?.data.funnels ?? [];
  const selected = selectedFunnel(controls.funnel, list, true) as string;
  const all = selected === ALL_FUNNELS;

  // Wait for the funnel list like the Email tab: before it loads `selected` is All for everyone, and a
  // link to one funnel would run the every-funnel query first and throw the answer away.
  const sources = useQuery({
    queryKey: statsSourcesKey(selected, controls.days),
    queryFn: () => (all ? getFunnelSourcesAll(controls.days) : getFunnelSources(selected, controls.days)),
    enabled: funnels.isSuccess,
  });

  if (funnels.isError) {
    return (
      <Alert>
        <AlertTitle>Landing page figures unavailable</AlertTitle>
        <AlertDescription>{messageFor(funnels.error, areaName)}</AlertDescription>
      </Alert>
    );
  }
  if (funnels.isPending) return <Skeleton className="h-96 w-full" />;

  const stats = sources.data?.data;
  const rows = stats ? sortedSources(stats.rows) : [];

  return (
    <div className="flex flex-col gap-4">
      <FunnelControls controls={controls} funnels={list} selected={selected} allowAll />
      {sources.isError ? (
        <OrdersUnavailable error={sources.error} areaName={areaName} />
      ) : (
        <>
          {stats && (
            <p className="text-base text-ks-ink" aria-live="polite">
              {sourcesSummaryLine(stats)}
            </p>
          )}
          <OrdersStamp
            dataAsOf={sources.data?.meta.data_as_of}
            caption="Orders are matched to the source a person signed up from, once a day."
          />
          <DataTable
            columns={sourceColumns(stats)}
            rows={rows}
            pending={sources.isPending}
            page={{ pageIndex: 0, pageSize: Math.max(rows.length, 1), rowCount: rows.length }}
            onPageChange={() => {}}
            emptyMessage={`No visits, sign-ups or orders from ${all ? "any funnel" : "this funnel"} in this window yet.`}
            caption={`Traffic sources for ${funnelLabel(selected)}, last ${controls.days} days`}
          />
          {stats?.app && (
            <>
              <p className="text-sm text-muted-foreground">
                In-app orders were placed in the app&apos;s own checkout. A buyer who also signed up on a lander counts
                in both Orders and In-app orders.
              </p>
              <DataTable
                columns={SEGMENT_COLUMNS}
                rows={stats.app.segments}
                pending={sources.isPending}
                page={{ pageIndex: 0, pageSize: Math.max(stats.app.segments.length, 1), rowCount: stats.app.segments.length }}
                onPageChange={() => {}}
                emptyMessage="No in-app orders in this window yet."
                caption={`In-app orders by app source and door, last ${controls.days} days`}
              />
            </>
          )}
        </>
      )}
    </div>
  );
}
